import {useState, useRef, useEffect} from 'react';
import IconButton, {IconButtonSize} from 'patient-ping-remedy/packages/icon-button';
import ToolTip from 'patient-ping-remedy/packages/tool-tip/lib';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {colors} from 'patient-ping-remedy/packages/theme';
import styled from '@emotion/styled';
import {useAlertStore} from '@/store/alert_store';
import {log} from '@/helpers/logger';

type Props = {
  value: string;
  ariaLabel: string;
  errorMessage?: string;
}

// Overrides the inner FontAwesome SVG color directly since IconButton
// doesn't expose color/hover props. Sets a muted default and highlights
// on hover to signal interactivity.
const StyledIconButton = styled(IconButton)`
  svg { color: ${colors.gray4}; }
  &:hover, &:focus-visible {
    background: ${colors.gray2};
    svg { color: ${colors.primary1}; }
  }
`;

const CopyButton = ({ value, ariaLabel, errorMessage }: Props) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const addAlert = useAlertStore((state) => state.addAlert);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Clipboard API may be unavailable (older browsers, insecure contexts,
      // or denied permissions). Surface the failure to the user.
      addAlert({
        type: ToastType.ERROR,
        content: errorMessage ?? 'Unable to copy. Your browser may not support copying.',
      });
      log('error', 'Failed to copy to clipboard', error);
    }
  };

  return (
    <span data-testid="copy-button">
      <ToolTip text={copied ? 'Copied!' : 'Copy'} type="dark">
        <StyledIconButton
          iconClass={ICONS.clone}
          size={IconButtonSize.SMALL}
          onClick={handleCopy}
          aria-label={ariaLabel}
        />
      </ToolTip>
    </span>
  );
};

export default CopyButton;
